import { createContext, useCallback, useContext, type ReactNode } from "react";

/** Same pattern IMS Admin Manager stores on organizations.slug. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * App section names and shell routes that cannot occupy the tenant
 * segment in /apps/{app}/{slug}/...
 */
export const RESERVED_APP_SEGMENTS = new Set([
  "account",
  "api",
  "app-designer",
  "apps",
  "calendar",
  "categories",
  "claims",
  "companies",
  "contacts",
  "dashboard",
  "dashboard-designer",
  "dashboards",
  "document-designer",
  "entries",
  "expense",
  "fields",
  "forecast",
  "help",
  "invoices",
  "lead-generator",
  "leads",
  "agreements",
  "login",
  "mailbox",
  "manager",
  "marketing",
  "opportunities",
  "orders",
  "products",
  "projects",
  "activities",
  "notifications",
  "reports",
  "search",
  "targets",
  "tasks",
  "quote-accept",
  "quotes",
  "sales-cycle",
  "settings",
  "setup-password",
  "reset-password",
  "signup",
  "sso",
  "submissions",
  "submitted",
  "support",
  "timesheet",
  "unsubscribe",
]);

function restPath(path: string): string {
  if (!path || path === "/") return "/";
  return path.startsWith("/") ? path : `/${path}`;
}

/** Canonical tenant app URL: /apps/crm/demo-org/companies */
export function appUrl(appCode: string, orgSlug: string | null | undefined, path = "/"): string {
  const rest = restPath(path);
  if (!orgSlug) {
    return rest === "/" ? `/apps/${appCode}` : `/apps/${appCode}${rest}`;
  }
  return rest === "/" ? `/apps/${appCode}/${orgSlug}` : `/apps/${appCode}/${orgSlug}${rest}`;
}

/**
 * Insert or replace the tenant slug after /apps/{appCode}/.
 * Identity-first: a mismatched slug is rewritten to this session's slug
 * rather than switching organizations.
 */
export function withOrgSlug(pathname: string, orgSlug: string): string {
  if (!orgSlug || !pathname.startsWith("/apps/")) return pathname;
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length < 2) return pathname;
  const appCode = parts[1];
  const next = parts[2] ?? "";
  const afterApp = parts.slice(2);
  if (!next) return appUrl(appCode, orgSlug);
  if (next === orgSlug) return pathname;
  if (RESERVED_APP_SEGMENTS.has(next) || !SLUG_PATTERN.test(next)) {
    return appUrl(appCode, orgSlug, `/${afterApp.join("/")}`);
  }
  const rest = parts.slice(3).join("/");
  return rest ? appUrl(appCode, orgSlug, `/${rest}`) : appUrl(appCode, orgSlug);
}

const CrmSlugContext = createContext("");

export function CrmSlugProvider({ slug, children }: { slug: string; children: ReactNode }) {
  return <CrmSlugContext.Provider value={slug}>{children}</CrmSlugContext.Provider>;
}

/** Build CRM paths. Empty slug (tests without a provider) keeps /apps/crm/... */
export function useCrmHref() {
  const slug = useContext(CrmSlugContext);
  return useCallback(
    (path: string) => {
      if (path.startsWith("/apps/crm")) return withOrgSlug(path, slug);
      return appUrl("crm", slug, path);
    },
    [slug],
  );
}
